import { SettingsContentCard } from '__components/Card/Card';
import styled from '@emotion/styled';
import {
  PageSizing,
  contentWidthCss,
} from '__components/Layout/PageContentWidth';
import PageToolbar, {
  PAGE_TOOLBAR_HEIGHT,
  PageSearchInput,
  PageToolbarSection,
  PageToolbarTitle,
} from '__components/Layout/PageToolbar';
import { useTranslation } from 'react-i18next';
import { COLUMN_SIZE, Grid } from '@kizen/kds/Grid';
import { useCallback, useContext, useMemo, useState } from 'react';
import { Tile } from './components/Tile';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import PluginService from 'services/PluginService';
import { PLUGINS } from 'queries/query-keys';
import { MarketplacePlugin } from './types';
import { PluginContext } from 'ts-components/Plugins/context';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import Loader from '__components/Kizen/Loader';
import { Typography } from '@kizen/kds/Typography';
import { useSetTitleOnLoad } from '__hooks/useSetTitleOnLoad';
import { Spacer } from '@kizen/kds/Spacer';
import { usePluginEnable } from './Details/usePluginEnable';
import TablePagination from '__components/Tables/Big/TablePagination';
import { AbsoluteLoader } from './components/SetupAssistant/styles';
import { PluginEnableModals } from './components/PluginEnableModals';
import { useMarketplacePermissions } from './permissions';
import { useBusinessPluginApps } from 'ts-components/Plugins/useBusinessPluginApps';

const StyledToolbar = styled.div`
  ${contentWidthCss}
  height: ${PAGE_TOOLBAR_HEIGHT - 6}px;
  margin-bottom: 6px;
`;

// Should be divisible by 3 since we do 3 columns
const PLUGINS_PER_PAGE = 12;

const MarketplacePage = () => {
  const { t } = useTranslation();

  useSetTitleOnLoad(t('App Marketplace'));

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const { canEditPlugins } = useMarketplacePermissions();

  const {
    data: pluginsData,
    isLoading: isAvailableSectionLoading,
    isRefetching: isAvailableSectionRefetching,
    isPlaceholderData: isAvailableSectionPlaceholder,
  } = useQuery<{
    results: MarketplacePlugin[];
    count: number;
  }>({
    queryFn: () =>
      PluginService.getPluginApps(searchTerm, false, page, PLUGINS_PER_PAGE),
    queryKey: PLUGINS.GET_PLUGIN_APPS(
      searchTerm,
      false,
      page,
      PLUGINS_PER_PAGE
    ),
    placeholderData: keepPreviousData,
    enabled: canEditPlugins,
  });

  const isAvailableSectionPending =
    isAvailableSectionLoading || isAvailableSectionPlaceholder;

  const { data: allBusinessPluginApps, isLoading } =
    useBusinessPluginApps(true);
  const businessPluginApps = useMemo(
    () => allBusinessPluginApps?.filter((bpa) => bpa.source !== 'business'),
    [allBusinessPluginApps]
  );

  const businessPluginAppsByApiName = useMemo(() => {
    return (
      businessPluginApps?.reduce((acc: any, curr: any) => {
        return {
          ...acc,
          [curr.plugin_app.api_name]: curr,
        };
      }, {}) ?? {}
    );
  }, [businessPluginApps]);

  const { plugins: installedPlugins, refreshPluginArtifacts } =
    useContext(PluginContext);

  const refetchAll = useCallback(
    (pluginApiName?: string) => refreshPluginArtifacts(pluginApiName),
    [refreshPluginArtifacts]
  );

  const pluginEnable = usePluginEnable({
    businessPluginAppsByApiName,
    refetch: refetchAll,
  });
  const { onChangeEnabled, pendingChange } = pluginEnable;

  const onChangeConfig = useCallback(
    async (pluginApiName: string, config: any) => {
      try {
        await PluginService.updateBusinessPluginApp(pluginApiName, {
          config,
        });
        await refetchAll(pluginApiName);
      } catch (ex) {
        monitoringExceptionHelper(ex);
      }
    },
    [refetchAll]
  );

  const marketplaceGridContent = useMemo(() => {
    if (!pluginsData?.results) {
      return [];
    }

    const res = pluginsData.results
      .sort((a, b) => {
        return a.name > b.name ? 1 : -1;
      })
      .reduce((acc, curr) => {
        const res = [...acc];
        const last = res[res.length - 1];

        if (!last || last.content.length === 3) {
          res.push({
            id: `row-${res.length}`,
            content: [],
            layout: [],
          });
        }

        const installation = installedPlugins?.[curr.api_name];
        const existingBusinessPluginApp =
          businessPluginAppsByApiName[curr.api_name];

        res[res.length - 1].layout.push(COLUMN_SIZE.THIRD_WIDTH);
        res[res.length - 1].content.push(
          <Tile
            key={curr.id}
            plugin={curr}
            installation={installation}
            onChangeEnabled={onChangeEnabled}
            onChangeConfig={onChangeConfig}
            overrideCheckedState={
              pendingChange.apiName === curr.api_name
                ? pendingChange.enabled
                : undefined
            }
            hasBusinessPluginApp={Boolean(existingBusinessPluginApp)}
            qa={{
              'plugin-id': curr.id,
              'plugin-name': curr.api_name,
            }}
          />
        );

        return res;
      }, [] as any);

    return res;
  }, [
    pluginsData,
    installedPlugins,
    onChangeEnabled,
    pendingChange,
    businessPluginAppsByApiName,
    onChangeConfig,
  ]);

  return (
    <PageSizing>
      <StyledToolbar>
        <PageToolbar>
          <PageToolbarSection>
            <PageSearchInput
              placeholder={t('Search Marketplace')}
              value={searchTerm}
              onChange={(term: string) => {
                setSearchTerm(term);

                // If the search term changes, go back to the first page
                if (term !== searchTerm) {
                  setPage(1);
                }
              }}
              loading={isAvailableSectionRefetching}
              isClearable
              data-qa-id="marketplace-search-input"
            />
          </PageToolbarSection>
          {pluginsData ? (
            <PageToolbarTitle
              count={pluginsData.count}
              single={t('App')}
              plural={t('Apps')}
            />
          ) : (
            <PageToolbarTitle>{t('App Marketplace')}</PageToolbarTitle>
          )}
          <PageToolbarSection />
        </PageToolbar>
      </StyledToolbar>
      <SettingsContentCard>
        {isLoading ? (
          <Loader loading />
        ) : (
          <>
            <div data-qa-section="available-apps">
              <div className="min-h-[75px] relative">
                {marketplaceGridContent.length > 0 ? (
                  <>
                    <Grid gap={20} config={marketplaceGridContent} />
                    <Spacer size={20} />
                    <TablePagination
                      page={page}
                      onChangePage={setPage}
                      perPage={PLUGINS_PER_PAGE}
                      showPageSizeOptions={false}
                      totalCount={pluginsData?.count}
                    />
                  </>
                ) : isAvailableSectionPending ? null : (
                  <div className="pt-12">
                    <Typography className="text-center">
                      {canEditPlugins
                        ? t('No Apps Found')
                        : t("You don't have permission to install apps.")}
                    </Typography>
                  </div>
                )}
                {isAvailableSectionLoading || isAvailableSectionRefetching ? (
                  <AbsoluteLoader loading transparent inline />
                ) : null}
              </div>
            </div>
          </>
        )}
      </SettingsContentCard>
      <PluginEnableModals
        pluginEnable={pluginEnable}
        businessPluginAppsByApiName={businessPluginAppsByApiName}
      />
      <Spacer size={30} />
    </PageSizing>
  );
};

export default MarketplacePage;
